import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  getFirestore,
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  getDocs, 
  Timestamp,
  Unsubscribe 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
// import { db, storage } from '../lib/firebase';
//import { storage } from '../../src/Components/lib/firebase';//./src/Components/lib/firebase
//import { storage } from 'firebase';
import {getStorage} from 'firebase/storage';
 
export const storage = getStorage(app)
const db = getFirestore(app);

// Types for dispatch
import type { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { app } from '../Components/lib/firebase';

// Message type alias for compatibility
export type Message = ChatMessage;

export interface ChatMessage {
  id?: string;
  senderEmail: string;
  receiverEmail: string;
  text?: string; // Changed from 'message' to 'text' to match UI
  message?: string; // Keep both for compatibility
  timestamp: Timestamp;
  type?: 'text' | 'image' | 'document'; // Add type field
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
}

interface ChatState {
  messages: ChatMessage[];
  loading: boolean;
  error: string | null;
  isListening: boolean;
  activeChat: {
    receiverEmail: string;
    receiverId: string;
  } | null;
}

const initialState: ChatState = {
  messages: [],
  loading: false,
  error: null,
  isListening: false,
  activeChat: null,
};

// Real-time listener management
let chatListener: Unsubscribe | null = null;

// Async thunk for sending messages
export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (messageData: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    try {
      // Filter out undefined values to prevent Firestore errors
      const cleanData = Object.fromEntries(
        Object.entries(messageData).filter(([, value]) => value !== undefined)
      );
      
      const docRef = await addDoc(collection(db, 'messages'), {
        ...cleanData,
        timestamp: Timestamp.now(),
      });
      return { id: docRef.id, ...messageData, timestamp: Timestamp.now() };
    } catch (error) {
      throw new Error(`Failed to send message: ${error}`);
    }
  }
);

// Async thunk for uploading files
export const uploadFile = createAsyncThunk(
  'chat/uploadFile',
  async ({ file, senderEmail, receiverEmail }: { 
    file: File; 
    senderEmail: string; 
    receiverEmail: string; 
  }) => {
    try {
      const fileRef = ref(storage, `chat-files/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      const messageData = {
        senderEmail,
        receiverEmail,
        text: `Shared a file: ${file.name}`,
        type: file.type.startsWith('image/') ? 'image' as const : 'document' as const,
        fileUrl: downloadURL,
        fileName: file.name,
        fileType: file.type,
      };

      const docRef = await addDoc(collection(db, 'messages'), {
        ...messageData,
        timestamp: Timestamp.now(),
      });

      return { id: docRef.id, ...messageData, timestamp: Timestamp.now() };
    } catch (error) {
      throw new Error(`Failed to upload file: ${error}`);
    }
  }
);

// Async thunk for loading chat history
export const loadChatHistory = createAsyncThunk(
  'chat/loadChatHistory',
  async ({ senderEmail, receiverEmail }: { senderEmail: string; receiverEmail: string }) => {
    try {
      const q = query(
        collection(db, 'messages'),
        where('senderEmail', 'in', [senderEmail, receiverEmail]),
        where('receiverEmail', 'in', [senderEmail, receiverEmail]),
        orderBy('timestamp', 'asc')
      );
      
      const querySnapshot = await getDocs(q);
      const messages: ChatMessage[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        messages.push({
          id: doc.id,
          ...data,
        } as ChatMessage);
      });
      
      return messages;
    } catch (error) {
      throw new Error(`Failed to load chat history: ${error}`);
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    updateMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },
    setListening: (state, action: PayloadAction<boolean>) => {
      state.isListening = action.payload;
    },
    setActiveChat: (state, action: PayloadAction<{ receiverEmail: string; receiverId: string }>) => {
      state.activeChat = action.payload;
    },
    clearChat: (state) => {
      state.messages = [];
      state.activeChat = null;
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to send message';
      })
      .addCase(uploadFile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadFile.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to upload file';
      })
      .addCase(loadChatHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadChatHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload;
      })
      .addCase(loadChatHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load chat history';
      });
  },
});

// Real-time listener functions
export const startChatListener = (
  senderEmail: string, 
  receiverEmail: string, 
  dispatch: Dispatch<UnknownAction>
) => {
  if (chatListener) {
    chatListener();
  }

  dispatch(setListening(true));

  const q = query(
    collection(db, 'messages'),
    where('senderEmail', 'in', [senderEmail, receiverEmail]),
    where('receiverEmail', 'in', [senderEmail, receiverEmail]),
    orderBy('timestamp', 'asc')
  );

  chatListener = onSnapshot(
    q,
    (querySnapshot) => {
      const messages: ChatMessage[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        messages.push({
          id: doc.id,
          ...data,
        } as ChatMessage);
      });
      dispatch(updateMessages(messages));
    },
    (error) => {
      console.error('Chat listener error:', error);
      dispatch(setError(`Real-time update failed: ${error.message}`));
      dispatch(setListening(false));
    }
  );
};

export const stopChatListener = (dispatch?: Dispatch<UnknownAction>) => {
  if (chatListener) {
    chatListener();
    chatListener = null;
  }
  if (dispatch) {
    dispatch(setListening(false));
  }
};

export const { updateMessages, setListening, setActiveChat, clearChat, clearMessages, setError } = chatSlice.actions;

// Default export for the reducer
export default chatSlice.reducer;
